import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import NavigationBar from '@/components/NavigationBar';
import Footer from '@/components/Footer';
import FAQSection from '@/components/FAQSection';
import { ChevronDown, ChevronUp, Check, ChevronLeft, ChevronRight, X, Download, ShoppingCart } from 'lucide-react';
import { submitInterest, addToCart, generateSignedUrl } from '../utils/api';
import { toast } from 'sonner';
import { useCartCount } from '@/hooks/useCartCount';
import ImageWithLoader from '@/components/ImageWithLoader';
import LoadingSpinner from '@/components/LoadingSpinner';

// Mock curation packages data
const curationPackagesData = {
  urban: {
    id: 'urban',
    name: 'Urban',
    price: 10000000,
    description: 'The Urban collection is designed for the modern achiever — refined, dynamic, and always on the move. Your perfect companion for weekday commutes, impression-making business meetings, and spontaneous weekend drives that turn heads wherever you go. Experience sophistication in motion.',
    cars: [
      {
        name: 'BMW X1',
        category: 'Commuter',
        days: 180,
        sharedOwners: 2,
        images: [
          'https://images.unsplash.com/photo-1677517859847-0e750bfd13a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxCTVclMjBYMXxlbnwwfHx8fDE3NjMwNTkyOTR8MA&ixlib=rb-4.1.0&q=85',
          'https://images.unsplash.com/photo-1680429039668-3ff5c1932f6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxCTVclMjBYMXxlbnwwfHx8fDE3NjMwNTkyOTR8MA&ixlib=rb-4.1.0&q=85',
          'https://images.unsplash.com/photo-1680298255666-6071fc905870?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxCTVclMjBYMXxlbnwwfHx8fDE3NjMwNTkyOTR8MA&ixlib=rb-4.1.0&q=85'
        ],
        description: 'The BMW X1 delivers premium compact SUV versatility for your urban commute. Combining BMW\'s signature driving dynamics with modern luxury, advanced technology, and efficient performance, it\'s the perfect companion for navigating city life with style and sophistication.',
        features: ['2.0L Turbo Engine - 228 HP', 'xDrive Intelligent AWD', 'BMW Curved Display', 'Premium LED Lighting', 'Spacious Interior']
      },
      {
        name: 'Land Rover Defender',
        category: 'Outstation',
        days: 90,
        sharedOwners: 4,
        images: [
          'https://images.unsplash.com/photo-1730830812273-12c0a8a98092?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxMYW5kJTIwUm92ZXIlMjBEZWZlbmRlcnxlbnwwfHx8fDE3NjMwNTkzMDZ8MA&ixlib=rb-4.1.0&q=85',
          'https://images.unsplash.com/photo-1522128494212-18250ea17a62?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxMYW5kJTIwUm92ZXIlMjBEZWZlbmRlcnxlbnwwfHx8fDE3NjMwNTkzMDZ8MA&ixlib=rb-4.1.0&q=85',
          'https://images.unsplash.com/photo-1579880251397-2c3ed174a774?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxMYW5kJTIwUm92ZXIlMjBEZWZlbmRlcnxlbnwwfHx8fDE3NjMwNTkzMDZ8MA&ixlib=rb-4.1.0&q=85'
        ],
        description: 'The Land Rover Defender is the ultimate icon of rugged capability meets modern luxury. Perfect for outstation adventures, it conquers any terrain with legendary off-road prowess while delivering refined comfort and cutting-edge technology. Adventure without compromise.',
        features: ['3.0L Inline-6 Turbocharged - 395 HP', 'Terrain Response 2', 'Wade Sensing', 'Pivi Pro Infotainment', 'Configurable Terrain Response']
      },
      {
        name: 'MG Cyberster',
        category: 'Weekender',
        days: 90,
        sharedOwners: 4,
        images: [
          'https://images.unsplash.com/photo-1719246355093-7261241ddae7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxNRyUyMEN5YmVyc3RlcnxlbnwwfHx8fDE3NjMwNTkzMTV8MA&ixlib=rb-4.1.0&q=85',
          'https://images.unsplash.com/photo-1719246355058-4249765206a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxNRyUyMEN5YmVyc3RlcnxlbnwwfHx8fDE3NjMwNTkzMTV8MA&ixlib=rb-4.1.0&q=85',
          'https://images.unsplash.com/photo-1752959837480-13298cebbd48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxNRyUyMEN5YmVyc3RlcnxlbnwwfHx8fDE3NjMwNTkzMTV8MA&ixlib=rb-4.1.0&q=85'
        ],
        description: 'The MG Cyberster is your futuristic electric roadster for exhilarating weekend escapes. With stunning scissor doors, cutting-edge EV technology, and thrilling performance, it delivers pure driving excitement with zero emissions. The future of open-top motoring.',
        features: ['Electric Dual Motor - 536 HP', '0-100 km/h in 3.2 seconds', 'Range: 580 km', 'Scissor Doors', 'Advanced Connectivity']
      }
    ]
  },
  grand: {
    id: 'grand',
    name: 'Grand',
    price: 20000000,
    description: 'The Grand collection blends comfort, capability, and charisma. Glide through city life in elegance, conquer the open roads in supreme confidence, and unleash pure exhilaration when the weekend arrives. Where every journey becomes a grand experience.',
    cars: [
      {
        name: 'Range Rover Velar',
        category: 'Commuter',
        days: 180,
        sharedOwners: 2,
        images: [
          'https://customer-assets.emergentagent.com/job_asset-ticket/artifacts/5x11dchh_RR%20Velar1.jpg',
          'https://customer-assets.emergentagent.com/job_asset-ticket/artifacts/z0l6jek2_RR%20Velar2.jpg',
          'https://customer-assets.emergentagent.com/job_asset-ticket/artifacts/2hvt49hm_RR%20Velar3.jpg'
        ],
        description: 'The Range Rover Velar combines contemporary design with refined luxury for your daily commute. Its minimalist elegance, cutting-edge technology, and commanding presence make every journey effortlessly sophisticated. Experience the perfect balance of modern aesthetics and British craftsmanship.',
        features: ['3.0L Supercharged V6 - 380 HP', 'Touch Pro Duo Infotainment', 'Matrix LED Headlights', 'Meridian Surround Sound', 'Premium Cabin Air Purification']
      },
      {
        name: 'Range Rover Autobiography LWB',
        category: 'Outstation',
        days: 90,
        sharedOwners: 4,
        images: [
          'https://customer-assets.emergentagent.com/job_asset-ticket/artifacts/w3pld7rs_AutoBio1.png',
          'https://customer-assets.emergentagent.com/job_asset-ticket/artifacts/rsstf8fr_AutoBio2.png',
          'https://customer-assets.emergentagent.com/job_asset-ticket/artifacts/a0a9iizy_RR%20Biography3.png'
        ],
        description: 'The Range Rover Autobiography Long Wheelbase represents the pinnacle of luxury SUVs. With extended rear legroom, executive seating, and unparalleled refinement, it transforms outstation journeys into first-class travel. Command any terrain while enjoying supreme comfort and prestige.',
        features: ['5.0L Supercharged V8 - 565 HP', 'Executive Class Rear Seating', 'Adaptive Air Suspension', 'Signature Interior Finishes', 'Terrain Response 2 Pro']
      },
      {
        name: 'Jaguar F-Type',
        category: 'Weekender',
        days: 90,
        sharedOwners: 4,
        images: [
          'https://customer-assets.emergentagent.com/job_asset-ticket/artifacts/7g5u58um_j%20f%20type%201.jpg',
          'https://customer-assets.emergentagent.com/job_asset-ticket/artifacts/ml1wfmt0_j%20f%20type%202.png',
          'https://customer-assets.emergentagent.com/job_asset-ticket/artifacts/2cw2ixjd_j%20f%20type%203.jpg'
        ],
        description: 'The Jaguar F-Type delivers pure British sports car exhilaration for your weekend adventures. With its sculpted design, thrilling exhaust note, and dynamic performance, every drive becomes an unforgettable experience. Embrace the perfect blend of elegance and adrenaline.',
        features: ['5.0L Supercharged V8 - 575 HP', 'Active Sport Exhaust', 'Adaptive Dynamics', 'Performance Seats', 'Convertible Soft Top']
      }
    ]
  },
  exotique: {
    id: 'exotique',
    name: 'Exotique',
    price: 30000000,
    description: 'The Exotique collection defines the pinnacle of curated luxury. Command presence, indulge in opulence, and celebrate your love for the extraordinary — whether it\'s the grace of a Maybach, the might of a Range Rover, or the thrill of an Aston Martin. For those who don\'t follow luxury — they define it.',
    cars: [
      {
        name: 'Range Rover Sport',
        category: 'Commuter',
        days: 180,
        sharedOwners: 2,
        images: [
          'https://images.unsplash.com/photo-1679506640590-f0152786dff0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxSYW5nZSUyMFJvdmVyJTIwU3BvcnR8ZW58MHx8fHwxNzYzMDU5MzI0fDA&ixlib=rb-4.1.0&q=85',
          'https://images.unsplash.com/photo-1638686302275-0e87df720aca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxSYW5nZSUyMFJvdmVyJTIwU3BvcnR8ZW58MHx8fHwxNzYzMDU5MzI0fDA&ixlib=rb-4.1.0&q=85',
          'https://images.unsplash.com/photo-1604054094723-3a949e4a8993?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxSYW5nZSUyMFJvdmVyJTIwU3BvcnR8ZW58MHx8fHwxNzYzMDU5MzI0fDA&ixlib=rb-4.1.0&q=85'
        ],
        description: 'The Range Rover Sport delivers dynamic luxury with commanding presence for your daily commute. Blending powerful performance, refined elegance, and advanced technology, it makes every journey an expression of confidence and sophistication. Performance meets prestige.',
        features: ['3.0L Supercharged V6 - 355 HP', 'Dynamic Response System', 'Adaptive Dynamics', 'Meridian Sound System', 'Premium Windsor Leather']
      },
      {
        name: 'Lexus LM',
        category: 'Outstation',
        days: 90,
        sharedOwners: 4,
        images: [
          'https://images.pexels.com/photos/248683/pexels-photo-248683.jpeg',
          'https://images.pexels.com/photos/21012245/pexels-photo-21012245.jpeg',
          'https://images.pexels.com/photos/29297781/pexels-photo-29297781.jpeg'
        ],
        description: 'The Lexus LM redefines ultra-luxury MPV travel for outstation journeys. With executive-class rear seating, unparalleled space, and whisper-quiet refinement, it transforms long drives into first-class lounge experiences. Luxury in motion, redefined.',
        features: ['2.5L Hybrid Engine - 250 HP', 'Executive Class Partition', 'Ottoman Rear Seats', 'Mark Levinson Audio', 'Advanced Climate Control']
      },
      {
        name: 'Aston Martin Vantage',
        category: 'Weekender',
        days: 90,
        sharedOwners: 4,
        images: [
          'https://images.unsplash.com/photo-1730365646968-baa17eb4406e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxBc3RvbiUyME1hcnRpbiUyMFZhbnRhZ2V8ZW58MHx8fHwxNzYzMDU5MzYzfDA&ixlib=rb-4.1.0&q=85',
          'https://images.unsplash.com/photo-1571753902016-ed0646fd891c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxBc3RvbiUyME1hcnRpbiUyMFZhbnRhZ2V8ZW58MHx8fHwxNzYzMDU5MzYzfDA&ixlib=rb-4.1.0&q=85',
          'https://images.unsplash.com/photo-1650741917348-e20527aed703?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxBc3RvbiUyME1hcnRpbiUyMFZhbnRhZ2V8ZW58MHx8fHwxNzYzMDU5MzYzfDA&ixlib=rb-4.1.0&q=85'
        ],
        description: 'The Aston Martin Vantage embodies pure British sports car excellence for your weekend adventures. With stunning design, exhilarating performance, and handcrafted luxury, it transforms every drive into an unforgettable experience. Where passion meets perfection.',
        features: ['4.0L Twin-Turbo V8 - 503 HP', '8-Speed ZF Automatic', 'Limited Slip Differential', 'Hand-Stitched Interior', 'Active Exhaust System']
      }
    ]
  },
  extravagance: {
    id: 'extravagance',
    name: 'Extravagance',
    price: 50000000,
    description: 'The Extravagance collection represents the absolute zenith of automotive luxury and performance. From the unmatched prestige of a Maybach, the commanding authority of a Range Rover SV, to the pure adrenaline of a Ferrari — this is luxury beyond limits. For those who accept nothing less than perfection.',
    cars: [
      {
        name: 'Mercedes Benz S Class Maybach',
        category: 'Commuter',
        days: 180,
        sharedOwners: 2,
        images: [
          'https://images.unsplash.com/photo-1749454006955-d677069782c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxNZXJjZWRlcyUyME1heWJhY2glMjBTLUNsYXNzfGVufDB8fHx8MTc2MzA1OTUyNnww&ixlib=rb-4.1.0&q=85',
          'https://images.pexels.com/photos/31040133/pexels-photo-31040133.jpeg',
          'https://images.pexels.com/photos/9275664/pexels-photo-9275664.jpeg'
        ],
        description: 'The Mercedes-Maybach S-Class represents the absolute pinnacle of automotive luxury and prestige. Every journey in this sanctuary on wheels is an experience in unparalleled comfort, cutting-edge technology, and timeless elegance. Where perfection meets power.',
        features: ['4.0L V8 Biturbo - 496 HP', 'Executive Rear Seating Plus', 'Burmester High-End 4D Sound', 'MBUX with AR Navigation', 'Exclusive Nappa Leather']
      },
      {
        name: 'Range Rover SV Autobiography',
        category: 'Outstation',
        days: 90,
        sharedOwners: 4,
        images: [
          'https://images.unsplash.com/photo-1649382801646-da9c4db0351e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxSYW5nZSUyMFJvdmVyJTIwbHV4dXJ5fGVufDB8fHx8MTc2MzA1OTU0Mnww&ixlib=rb-4.1.0&q=85',
          'https://images.pexels.com/photos/116675/pexels-photo-116675.jpeg',
          'https://images.unsplash.com/photo-1550523164-e0330d1775b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxSYW5nZSUyMFJvdmVyJTIwbHV4dXJ5fGVufDB8fHx8MTc2MzA1OTU0Mnww&ixlib=rb-4.1.0&q=85'
        ],
        description: 'The Range Rover SV Autobiography is the ultimate expression of Range Rover luxury and capability. Handcrafted by Special Vehicle Operations, it combines bespoke refinement with legendary all-terrain prowess. Conquer any journey in supreme comfort and commanding style.',
        features: ['5.0L Supercharged V8 - 557 HP', 'SV Bespoke Interior', 'Adaptive Air Suspension', 'Signature Sound System', 'All-Terrain Progress Control']
      },
      {
        name: 'Ferrari 296 GTB',
        category: 'Weekender',
        days: 90,
        sharedOwners: 4,
        images: [
          'https://images.unsplash.com/photo-1721139430306-a13f8a6a6d5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxGZXJyYXJpJTIwMjk2JTIwR1RCfGVufDB8fHx8MTc2MzA1OTU1Mnww&ixlib=rb-4.1.0&q=85',
          'https://images.unsplash.com/photo-1739985630355-192141c86632?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxGZXJyYXJpJTIwMjk2JTIwR1RCfGVufDB8fHx8MTc2MzA1OTU1Mnww&ixlib=rb-4.1.0&q=85',
          'https://images.pexels.com/photos/18415624/pexels-photo-18415624.jpeg'
        ],
        description: 'The Ferrari 296 GTB is a revolutionary hybrid supercar that delivers pure Ferrari performance with electrifying efficiency. With breathtaking design, a spine-tingling V6 soundtrack, and blistering acceleration, it redefines the supercar experience for the modern era.',
        features: ['3.0L V6 Hybrid - 818 HP', '0-100 km/h in 2.9 seconds', 'F1-Derived Technology', 'Active Aerodynamics', 'Pure Ferrari DNA']
      }
    ]
  }
};

const CurationDetailPage = ({ user }) => {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { cartCount } = useCartCount(user);
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCar, setExpandedCar] = useState(null);
  const [carImageIndices, setCarImageIndices] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://dashboard-flow-5.preview.emergentagent.com';
  
  // Fetch package data
  useEffect(() => {
    fetchPackageData();
    window.scrollTo(0, 0);
  }, [packageId]);

  const fetchPackageData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/curation-packages/${packageId}`);
      
      if (!response.ok) {
        // If not found in backend, fallback to hardcoded data
        const hardcodedData = curationPackagesData[packageId];
        if (hardcodedData) {
          setPackageData(hardcodedData);
        } else {
          throw new Error('Package not found');
        }
      } else {
        const data = await response.json();
        // Transform backend data to match expected format
        const transformedData = {
          id: data.slug || data.id || packageId,
          name: data.name,
          price: data.complete_package_price || data.price_per_day * 360 || 0,
          description: data.description,
          brochure_url: data.brochure_url || null,  // Include brochure URL from backend
          cars: data.vehicles && data.vehicles.length > 0 ? data.vehicles.map(v => ({
            name: v.name,
            category: v.segment,
            days: v.usage_days,
            sharedOwners: v.shared_owners,
            images: v.images || [],
            description: v.description,
            features: v.specifications || []
          })) : (curationPackagesData[packageId]?.cars || [])
        };
        setPackageData(transformedData);
      }
    } catch (error) {
      console.error('Error fetching package:', error);
      // Fallback to hardcoded data
      const hardcodedData = curationPackagesData[packageId];
      if (hardcodedData) {
        setPackageData(hardcodedData);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-gray">Loading package details...</p>
        </div>
      </div>
    );
  }
  
  if (!packageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center">
          <p className="text-xl text-gray mb-4">Package not found</p>
          <Button onClick={() => navigate('/high-assets')} className="bg-black text-white">
            Browse Packages
          </Button>
        </div>
      </div>
    );
  }

  const toggleCarExpansion = (index) => {
    setExpandedCar(expandedCar === index ? null : index);
  };
  
  const handleBack = () => {
    navigate(-1);
  };
  
  const handlePrevImage = (carIndex, totalImages) => {
    setCarImageIndices(prev => ({
      ...prev,
      [carIndex]: ((prev[carIndex] || 0) - 1 + totalImages) % totalImages
    }));
  };
  
  const handleNextImage = (carIndex, totalImages) => {
    setCarImageIndices(prev => ({
      ...prev,
      [carIndex]: ((prev[carIndex] || 0) + 1) % totalImages
    }));
  };
  
  const handleDownloadBrochure = async () => {
    // Check if brochure is available
    if (!packageData?.brochure_url) {
      toast.info('Brochure is not available for this package yet. Contact us for detailed information.');
      return;
    }

    try {
      // Show loading toast
      const loadingToast = toast.loading('Preparing brochure download...');
      
      // Generate signed URL for the PDF
      const response = await generateSignedUrl(packageData.brochure_url);
      const signedUrl = response.data.signed_url;
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Create a temporary link element for download
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = `${packageData.name.replace(/\s+/g, '-')}-Curation-Package-Brochure.pdf`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Brochure download started!');
      
      // Log download event (optional analytics)
      console.log('Curation Package Brochure downloaded:', packageData.name);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Unable to download brochure. Please try again later.');
    }
  };
  
  const handleExpressInterest = async () => {
    // Check if user is logged in
    if (!user) {
      toast.error('Please login to express interest');
      navigate('/login');
      return;
    }
    
    setSubmitting(true);
    try {
      await addToCart({
        asset_id: packageData.id,
        item_type: 'curation_package',
        tickets: 1
      });
      
      // Dispatch cart update event for navigation bar
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      
      // Show success modal
      setShowSuccessModal(true);
    } catch (error) {
      toast.error('Failed to add to cart. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const closeSuccessModal = () => {
    setShowSuccessModal(false);
  };

  return (
    <div className="min-h-screen bg-cream">
      <NavigationBar user={user} cartCount={cartCount} />
      
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 md:p-8 animate-in fade-in zoom-in duration-200">
            {/* Success Icon */}
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-green-600" />
            </div>

            {/* Title */}
            <h2 className="text-xl md:text-2xl font-bold text-center mb-3">
              Thank You for Your Interest!
            </h2>

            {/* Message */}
            <p className="text-gray-700 text-center mb-6 leading-relaxed">
              Thank you for your interest in the <span className="font-semibold">{packageData?.name} Package</span>. Our team will reach out to you shortly to assist you. You can view the status of your ticket in the Dashboard.
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/member-dashboard?tab=cart');
                }}
                className="flex-1 bg-black text-white hover:bg-black/90"
              >
                View in Dashboard
              </Button>
              <Button
                onClick={() => setShowSuccessModal(false)}
                variant="outline"
                className="flex-1 border-black text-black hover:bg-black hover:text-white"
              >
                Continue Browsing
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 md:py-16 lg:py-20">
        {/* Back and Contact Us Buttons */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <Button 
            onClick={handleBack}
            className="border border-black bg-white text-black hover:bg-black hover:text-white transition-all text-sm sm:text-base"
          >
            ← Back to High Assets
          </Button>
          <Button 
            onClick={() => document.getElementById('get-in-touch')?.scrollIntoView({ behavior: 'smooth' })}
            className="border border-black bg-white text-black hover:bg-black hover:text-white transition-all text-sm sm:text-base"
          >
            Contact Us
          </Button>
        </div>

        {/* Package Header */}
        <div className="bg-white rounded-lg p-4 sm:p-6 md:p-8 lg:p-12 shadow-lg mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 uppercase tracking-wide">{packageData.name} Collection</h1>
          <p className="text-base sm:text-lg md:text-xl text-gray mb-4 sm:mb-6">{packageData.description}</p>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between border-t pt-4 sm:pt-6 gap-4 lg:gap-6">
            <div>
              <span className="text-gold text-2xl sm:text-3xl md:text-4xl font-bold">₹{packageData.price.toLocaleString('en-IN')}</span>
              <p className="text-gray mt-1 sm:mt-2 text-sm sm:text-base">Complete 360-Day Curation Package</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                onClick={handleDownloadBrochure}
                className="bg-white text-black border-2 border-black hover:bg-black hover:text-white px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-6 text-sm sm:text-base md:text-lg shadow-lg flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Download Brochure</span>
                <span className="sm:hidden">Brochure</span>
              </Button>
              <Button 
                onClick={handleExpressInterest}
                disabled={submitting}
                className="bg-black text-white hover:bg-black/90 px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-6 text-sm sm:text-base md:text-lg shadow-lg"
              >
                {submitting ? 'Submitting...' : <><span className="hidden lg:inline">Express Interest for {packageData.name} Package</span><span className="lg:hidden">Express Interest</span></>}
              </Button>
            </div>
          </div>
        </div>

        {/* Cars Grid */}
        <div className="mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8">Your 3 Cars</h2>
          <div className="space-y-6">
            {packageData.cars.map((car, index) => {
              const currentImageIndex = carImageIndices[index] || 0;
              const totalImages = car.images.length;
              
              return (
                <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                    {/* Image Gallery */}
                    <div className="relative w-full h-64 sm:h-72 md:h-80 bg-gray-100">
                      <img 
                        src={car.images[currentImageIndex]} 
                        alt={`${car.name} - Image ${currentImageIndex + 1}`} 
                        className="w-full h-full object-cover" 
                      />
                      
                      {/* Navigation Arrows */}
                      {totalImages > 1 && (
                        <>
                          <button
                            onClick={() => handlePrevImage(index, totalImages)}
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                            aria-label="Previous image"
                          >
                            <ChevronLeft className="w-6 h-6" />
                          </button>
                          <button
                            onClick={() => handleNextImage(index, totalImages)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                            aria-label="Next image"
                          >
                            <ChevronRight className="w-6 h-6" />
                          </button>
                          
                          {/* Image Counter */}
                          <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                            {currentImageIndex + 1} / {totalImages}
                          </div>
                        </>
                      )}
                    </div>
                    
                    <div className="p-4 sm:p-6 md:p-8 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-3 sm:mb-4">
                          <span className="text-gold text-sm sm:text-base md:text-lg font-bold uppercase">
                            {car.category}
                          </span>
                          <span className="text-gray text-xs sm:text-sm"><span className="font-bold">{car.days} days/year</span></span>
                        </div>
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">{car.name}</h3>
                        <p className="text-gray mb-3 sm:mb-4 text-sm sm:text-base">{car.description}</p>
                        <div className="bg-cream p-4 rounded-lg mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="font-semibold">Usage Days:</span>
                            <span className="font-bold">{car.days} days/year</span>
                          </div>
                          <div className="flex justify-between text-sm mt-2">
                            <span className="font-semibold">Shared Owners:</span>
                            <span>{car.sharedOwners} members</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleCarExpansion(index)}
                        className="flex items-center justify-between w-full text-left font-semibold text-gold hover:text-gold/80"
                      >
                        <span>{expandedCar === index ? 'Hide' : 'Show'} Features & Specifications</span>
                        {expandedCar === index ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  {expandedCar === index && (
                    <div className="border-t border-gray-200 p-8 bg-cream">
                      <h4 className="font-bold text-lg mb-4">Features & Specifications</h4>
                      <ul className="grid md:grid-cols-2 gap-3">
                        {car.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center">
                            <Check className="w-5 h-5 text-gold mr-2 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* FAQs Section - Dynamic from Admin Portal */}
        <FAQSection 
          category="curated-assets" 
          title="Frequently Asked Questions" 
        />

        {/* Bottom CTA */}
        <div className="mt-8 sm:mt-12 text-center bg-black text-white rounded-lg p-6 sm:p-8 md:p-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">Ready to Experience the {packageData.name} Collection?</h2>
          <p className="text-sm sm:text-base md:text-lg mb-6 sm:mb-8 text-white/80">Join The Haute Circle and elevate your automotive lifestyle</p>
          <Button 
            onClick={handleExpressInterest}
            disabled={submitting}
            className="bg-white text-black hover:bg-white/90 px-6 sm:px-8 py-4 sm:py-6 text-sm sm:text-base md:text-lg shadow-lg w-full sm:w-auto"
          >
            {submitting ? 'Submitting...' : <><span className="hidden sm:inline">Express Interest for {packageData.name} Package</span><span className="sm:hidden">Express Interest</span></>}
          </Button>
        </div>
      </div>

      {/* Footer */}
      <div id="get-in-touch">
        <Footer />
      </div>
    </div>
  );
};

export default CurationDetailPage;
